import type { ButtonProps } from '@/shared/types/button';
import type { VisualSlotType } from '@/shared/types/types';
import {
  getResolvedTooltipLabel,
  resolveButtonTooltipProps,
  type ButtonTooltipSource,
} from '@/shared/utils/resolveButtonTooltipProps';
import type { ToolbarAddon, ToolbarButton } from '../types';

type ToolbarActionLike = Pick<
  ToolbarButton,
  'label' | 'icon' | 'color' | 'disabled' | 'pill' | 'tooltip' | 'tooltipPosition' | 'tooltipAlignment' | 'tooltipDelay'
>;

/** Props de icono/slots coherentes con la lógica del Toolbar. */
export function getToolbarActionIconProps(action: Pick<ToolbarActionLike, 'label' | 'icon'>) {
  const iconValue = action.label ? undefined : (action.icon as VisualSlotType | undefined);
  const leftSlotValue =
    action.label && action.icon ? (action.icon as VisualSlotType) : undefined;
  return { icon: iconValue, leftSlot: leftSlotValue };
}

/** Tooltip + `pointer={false}` cuando disabled para que el hover siga funcionando. */
export function getToolbarActionButtonProps(
  action: ToolbarActionLike & ButtonTooltipSource,
  extra?: Pick<ButtonProps, 'onClick'>
): Pick<
  ButtonProps,
  'color' | 'size' | 'icon' | 'leftSlot' | 'disabled' | 'pill' | 'tooltip' | 'tooltipProps' | 'pointer' | 'onClick'
> {
  const tooltipProps = resolveButtonTooltipProps(action);
  const tooltipLabel = getResolvedTooltipLabel(tooltipProps);
  const disabled = action.disabled;

  return {
    color: action.color || 'secondary',
    size: 'xs',
    ...getToolbarActionIconProps(action),
    disabled,
    pill: action.pill,
    ...tooltipProps,
    ...(disabled && tooltipLabel ? { pointer: false } : {}),
    ...extra,
  };
}

/** Addon tipo botón — misma resolución de tooltip que los botones principales. */
export function getToolbarAddonButtonProps(
  addon: ToolbarAddon,
  extra?: Pick<ButtonProps, 'onClick'>
) {
  return getToolbarActionButtonProps(
    {
      label: addon.label,
      icon: addon.icon,
      color: addon.color,
      disabled: addon.disabled,
      pill: addon.pill,
      tooltip: addon.tooltip,
      tooltipPosition: addon.tooltipPosition,
      tooltipAlignment: addon.tooltipAlignment,
      tooltipDelay: addon.tooltipDelay,
    },
    extra
  );
}
